import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isInTeams, initializeTeams } from '../utils/teamsUtils';
import * as microsoftTeams from '@microsoft/teams-js';
import { authConfig } from '../config/authConfig';
import { api } from '../services/api';
import { UserProfile } from '../types';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserProfile;
  isTeams: boolean;
  isTeamsInitialized: boolean;
  hasLicenseError: boolean;
  licenseStatus: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  setIsAuthenticated: (value: boolean) => void;
  setUser: (user: UserProfile) => void;
  setIsLoading: (value: boolean) => void;
  setHasLicenseError: (value: boolean) => void;
  setLicenseStatus: (status: string | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Setup API interceptor for license errors
const setupApiInterceptors = (
  setHasLicenseError: (value: boolean) => void,
  setLicenseStatus: (status: string | null) => void,
  navigate: (path: string) => void
) => {
  // Add a response interceptor
  api.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      if (error.response) {
        // Handle license errors (403 responses with licenseError flag)
        if (
          error.response.status === 403 &&
          error.response.data &&
          error.response.data.licenseError
        ) {
          setHasLicenseError(true);
          setLicenseStatus(error.response.data.licenseStatus || null);
          navigate('/license-error');
          return Promise.reject(error);
        }
      }
      return Promise.reject(error);
    }
  );
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isTeams, setIsTeams] = useState(false);
  const [isTeamsInitialized, setIsTeamsInitialized] = useState(false);
  const [hasLicenseError, setHasLicenseError] = useState(false);
  const [licenseStatus, setLicenseStatus] = useState<string | null>(null);
  const navigate = useNavigate();

  // Setup API interceptors for license errors
  useEffect(() => {
    setupApiInterceptors(setHasLicenseError, setLicenseStatus, navigate);
  }, [navigate]);

  // Add periodic user data refresh to sync with Azure AD changes
  useEffect(() => {
    if (!isAuthenticated) return;

    const refreshUserData = async () => {
      try {
        const response = await api.get('/auth/verify');
        const userData = response.data.data;
        setUser(userData.user);
      } catch (error) {
        console.error('Error refreshing user data:', error);
        // If refresh fails, don't logout immediately - token might still be valid
      }
    };

    // Refresh user data every 30 minutes
    const interval = setInterval(refreshUserData, 30 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);
      const teamsCheck = isInTeams();
      setIsTeams(teamsCheck);

      // Skip auth check for public routes (feedback forms, etc.)
      // These routes don't require authentication:
      // - /feedback: Strategic Performance Module 360° Feedback Form (NEW module)
      //   Location: src/pages/strategic_performance/pages/FeedbackForm.tsx
      //   Access: Token-based via ?token=accessToken query param
      //   Purpose: External contributors provide 360° feedback without logging in
      // - /feedback/submit: Legacy feedback submission (OLD Performance Management module)
      //   Location: src/pages/feedback/submit
      //   Access: Query param ?id=feedbackId
      // - /organization-ceo-feedback/submit: Organization CEO feedback submission
      //   Location: src/pages/organization_ceo_feedback/SubmitFeedback
      const publicRoutes = ['/feedback', '/feedback/submit', '/organization-ceo-feedback/submit'];
      const currentPath = window.location.pathname;
      const isPublicRoute = publicRoutes.some(route => currentPath.startsWith(route));
      
      if (isPublicRoute) {
        // For public routes, just set loading to false and skip auth
        setIsLoading(false);
        setIsTeamsInitialized(teamsCheck ? false : true);
        if (teamsCheck) {
          try {
            await initializeTeams();
            setIsTeamsInitialized(true);
          } catch (error) {
            console.error('Teams initialization failed:', error);
            setIsTeamsInitialized(false);
          }
        }
        return;
      }

      // Check for existing token in sessionStorage
      const existingToken = sessionStorage.getItem('auth_token');
      
      if (existingToken) {
        try {
          // Validate the token with the server and get fresh user data
          const response = await api.get('/auth/verify');
          const userData = response.data.data;
          // Token is valid, update auth state with fresh data
          setUser(userData.user);
          setIsAuthenticated(true);
          } catch (error) {
          console.error('Error validating existing token:', error);
          // Token validation failed, clear it
          sessionStorage.removeItem('auth_token');
          setIsAuthenticated(false);
          setUser(null);
        }
      }

      if (teamsCheck) {
        try {
          await initializeTeams();
          setIsTeamsInitialized(true);
        } catch (error) {
          console.error('Teams initialization failed:', error);
          setIsTeamsInitialized(false);
        }
      } else {
        setIsTeamsInitialized(true);
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const handleTeamsSSO = async () => {
    try {
      const authTokenRequest = {
        successCallback: (token: string) => {
          handleToken(token);
        },
        failureCallback: (error: string) => {
          console.error('Teams SSO failed:', error);
          setIsLoading(false);
        },
        resources: [`api://app.teamscorecards.online/${authConfig.clientId}`]
      };
      await microsoftTeams.authentication.getAuthToken(authTokenRequest);
    } catch (error) {
      console.error('Teams SSO error:', error);
      setIsLoading(false);
    }
  };

  const handleStandardLogin = () => {
    const loginUrl = authConfig.getLoginUrl();
    window.location.href = loginUrl;
  };

  const handleToken = async (token: string) => {
    try {
      
      const response = await api.post('/auth/callback', { token });

      const userData = response.data.data;
      
      // Store the token first
      sessionStorage.setItem('auth_token', userData.token);
      
      // Set initial user state
      setUser(userData.user);
      setIsAuthenticated(true);
      setHasLicenseError(false); // Reset license error state
      setLicenseStatus(null);     // Reset license status
      
      setIsLoading(false);
      
      // Only navigate if we're not already on the home page
      if (window.location.pathname !== '/') {
        navigate('/');
      }
    } catch (error: any) {
      console.error('Token handling failed:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      // Handle consent required error
      if (
        error.response && 
        error.response.status === 403 && 
        error.response.data && 
        error.response.data.error === 'consent_required'
      ) {
        setIsLoading(false);
        navigate('/consent', { 
          state: { 
            consentUrl: error.response.data.consentUrl,
            tenantId: error.response.data.tenantId
          } 
        });
        return;
      }

      // Check for license errors
      if (
        error.response && 
        error.response.status === 403 && 
        error.response.data && 
        error.response.data.licenseError
      ) {
        setHasLicenseError(true);
        setLicenseStatus(error.response.data.licenseStatus || null);
        navigate('/license-error');
        setIsLoading(false);
        return;
      }

      // Clear any potentially invalid token and stop loading; do not rethrow
      // so that the runtime error overlay does not appear on login failure
      sessionStorage.removeItem('auth_token');
      setIsLoading(false);
    }
  };

  const login = async () => {
    setIsLoading(true);
    if (isTeams && isTeamsInitialized) {
      await handleTeamsSSO();
    } else {
      handleStandardLogin();
    }
  };

  const refreshUserData = async () => {
    if (!isAuthenticated) return;
    
    try {
      const response = await api.get('/auth/verify');
      const userData = response.data.data;
      setUser(userData.user);
    } catch (error) {
      console.error('Error refreshing user data:', error);
      // If refresh fails, the token might be invalid
      // You could optionally logout here or show a warning
    }
  };

  const logout = async () => {
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('impersonator_token');
    sessionStorage.removeItem('impersonation_info');
    setUser(null);
    setIsAuthenticated(false);
    setHasLicenseError(false);
    setLicenseStatus(null);
    setIsLoading(false);
    navigate('/login');
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      isLoading, 
      user, 
      isTeams, 
      isTeamsInitialized,
      hasLicenseError,
      licenseStatus,
      login, 
      logout,
      refreshUserData,
      setIsAuthenticated,
      setUser,
      setIsLoading,
      setHasLicenseError,
      setLicenseStatus
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 